import React, { useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PredictionData {
  currentScore: number;
  predictedScore: number;
  confidence: number;
  trend: 'improving' | 'stable' | 'declining';
  factors: {
    name: string;
    impact: number;
    description: string;
    icon: string;
    color: string;
  }[];
}

interface SubjectPrediction {
  subject: string;
  current: number;
  predicted: number;
  change: number;
  color: string;
}

const PredictiveScoreAnalysis: React.FC = () => {
  const [examType] = useState<string>('JEE Main');
  
  const [overallPrediction] = useState<PredictionData>({
    currentScore: 245,
    predictedScore: 278,
    confidence: 85,
    trend: 'improving',
    factors: [
      {
        name: 'Study Consistency',
        impact: 35,
        description: 'Your 7-day study streak is significantly improving predictions',
        icon: 'fas fa-fire',
        color: '#27ae60'
      },
      {
        name: 'Accuracy Trend',
        impact: 28,
        description: 'Recent accuracy improvements indicate strong preparation',
        icon: 'fas fa-bullseye',
        color: '#667eea'
      },
      {
        name: 'Time Management',
        impact: 22,
        description: 'Faster question solving with maintained accuracy',
        icon: 'fas fa-clock',
        color: '#2d9cdb'
      },
      {
        name: 'Weak Areas',
        impact: -15,
        description: 'Organic Chemistry needs more attention',
        icon: 'fas fa-exclamation-triangle',
        color: '#e74c3c'
      }
    ]
  });

  const [subjectPredictions] = useState<SubjectPrediction[]>([
    { subject: 'Physics', current: 85, predicted: 92, change: +7, color: '#667eea' },
    { subject: 'Chemistry', current: 72, predicted: 78, change: +6, color: '#f39c12' },
    { subject: 'Mathematics', current: 88, predicted: 95, change: +7, color: '#2d9cdb' }
  ]);

  const [historicalData] = useState({
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8'],
    actual: [210, 218, 225, 230, 235, 240, 245, 0],
    predicted: [215, 220, 228, 235, 242, 250, 260, 278]
  });

  const [performanceDistribution] = useState({
    labels: ['0-60', '60-120', '120-180', '180-240', '240-300'],
    data: [5, 15, 25, 35, 20],
    predictedPercentile: 78
  });

  // Line Chart Data for Score Prediction
  const lineChartData = {
    labels: historicalData.labels,
    datasets: [
      {
        label: 'Actual Score',
        data: historicalData.actual,
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 3,
        pointRadius: 5,
        pointBackgroundColor: '#667eea',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Predicted Score',
        data: historicalData.predicted,
        borderColor: '#27ae60',
        backgroundColor: 'rgba(39, 174, 96, 0.1)',
        borderWidth: 3,
        borderDash: [5, 5],
        pointRadius: 5,
        pointBackgroundColor: '#27ae60',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 300,
        ticks: {
          stepSize: 50
        }
      }
    }
  };

  // Bar Chart Data for Performance Distribution
  const barChartData = {
    labels: performanceDistribution.labels,
    datasets: [
      {
        label: 'Student Distribution (%)',
        data: performanceDistribution.data,
        backgroundColor: [
          'rgba(231, 76, 60, 0.6)',
          'rgba(243, 156, 18, 0.6)',
          'rgba(241, 196, 15, 0.6)',
          'rgba(45, 156, 219, 0.6)',
          'rgba(39, 174, 96, 0.6)'
        ],
        borderColor: [
          '#e74c3c',
          '#f39c12',
          '#f1c40f',
          '#2d9cdb',
          '#27ae60'
        ],
        borderWidth: 2
      }
    ]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.parsed.y}% of students`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => value + '%'
        }
      }
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return 'fas fa-arrow-up';
      case 'declining': return 'fas fa-arrow-down';
      default: return 'fas fa-minus';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving': return '#27ae60';
      case 'declining': return '#e74c3c';
      default: return '#f39c12';
    }
  };

  return (
    <div className="predictive-score-analysis-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div className="card-header bg-transparent border-0 p-4">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="fw-bold mb-1">
                <i className="fas fa-chart-line me-2 text-primary"></i>
                AI Score Prediction
              </h4>
              <small className="text-muted">Advanced analytics powered by machine learning</small>
            </div>
            <div className="dropdown">
              <button 
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                type="button"
                data-bs-toggle="dropdown"
              >
                <i className="fas fa-graduation-cap me-2"></i>
                {examType}
                <i className="fas fa-chevron-down ms-2"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" onClick={() => {}}>JEE Main</button></li>
                <li><button className="dropdown-item" onClick={() => {}}>JEE Advanced</button></li>
                <li><button className="dropdown-item" onClick={() => {}}>NEET</button></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="card-body p-4">
          {/* Score Prediction Overview */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div 
                className="p-4 rounded-4 text-center"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                <small className="d-block mb-2 opacity-75">Current Score</small>
                <div className="display-4 fw-bold mb-2">{overallPrediction.currentScore}</div>
                <small className="opacity-75">out of 300</small>
              </div>
            </div>
            <div className="col-md-4">
              <div 
                className="p-4 rounded-4 text-center"
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                  color: 'white'
                }}
              >
                <small className="d-block mb-2 opacity-75">Predicted Score</small>
                <div className="display-4 fw-bold mb-2">{overallPrediction.predictedScore}</div>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <i className={getTrendIcon(overallPrediction.trend)}></i>
                  <small>+{overallPrediction.predictedScore - overallPrediction.currentScore} points</small>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div 
                className="p-4 rounded-4 text-center"
                style={{
                  background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
                  color: 'white'
                }}
              >
                <small className="d-block mb-2 opacity-75">Confidence Level</small>
                <div className="display-4 fw-bold mb-2">{overallPrediction.confidence}%</div>
                <div className="progress mt-2" style={{ height: '6px', background: 'rgba(255,255,255,0.3)' }}>
                  <div 
                    className="progress-bar"
                    style={{ width: `${overallPrediction.confidence}%`, background: 'white' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Score Trend Chart */}
          <div className="mb-4">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-chart-area me-2 text-primary"></i>
              Score Progression & Prediction
            </h6>
            <div style={{ height: '300px' }}>
              <Line data={lineChartData} options={lineChartOptions} />
            </div>
          </div>

          {/* Impact Factors */}
          <div className="mb-4">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-lightbulb me-2 text-warning"></i>
              Key Factors Affecting Your Score
            </h6>
            <div className="row g-3">
              {overallPrediction.factors.map((factor, index) => (
                <div key={index} className="col-md-6">
                  <div 
                    className="p-3 rounded-4"
                    style={{
                      background: factor.color + '15',
                      border: `2px solid ${factor.color}30`
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: '40px',
                          height: '40px',
                          background: factor.color + '30',
                          color: factor.color
                        }}
                      >
                        <i className={factor.icon}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{factor.name}</div>
                        <small className="text-muted">{factor.description}</small>
                      </div>
                      <div 
                        className="fw-bold"
                        style={{ 
                          color: factor.impact > 0 ? '#27ae60' : '#e74c3c',
                          fontSize: '18px'
                        }}
                      >
                        {factor.impact > 0 ? '+' : ''}{factor.impact}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subject-wise Predictions */}
          <div className="mb-4">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-book me-2 text-info"></i>
              Subject-wise Score Predictions
            </h6>
            <div className="row g-3">
              {subjectPredictions.map((subject, index) => (
                <div key={index} className="col-md-4">
                  <div 
                    className="p-4 rounded-4"
                    style={{
                      background: subject.color + '15',
                      border: `2px solid ${subject.color}30`
                    }}
                  >
                    <h6 className="fw-bold mb-3">{subject.subject}</h6>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <small className="text-muted d-block">Current</small>
                        <div className="h4 fw-bold mb-0">{subject.current}</div>
                      </div>
                      <div className="text-center">
                        <i 
                          className="fas fa-arrow-right fa-2x"
                          style={{ color: subject.color, opacity: 0.5 }}
                        ></i>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Predicted</small>
                        <div className="h4 fw-bold mb-0" style={{ color: subject.color }}>
                          {subject.predicted}
                        </div>
                      </div>
                    </div>
                    <div 
                      className="badge rounded-pill w-100 py-2"
                      style={{
                        background: subject.color + '30',
                        color: subject.color,
                        fontWeight: 600
                      }}
                    >
                      {subject.change > 0 ? '+' : ''}{subject.change} points improvement
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Distribution */}
          <div className="mb-4">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-users me-2 text-success"></i>
              Expected Performance Distribution
            </h6>
            <div className="row align-items-center">
              <div className="col-md-8">
                <div style={{ height: '250px' }}>
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              </div>
              <div className="col-md-4">
                <div 
                  className="p-4 rounded-4 text-center"
                  style={{
                    background: 'linear-gradient(135deg, #2d9cdb 0%, #1e87c7 100%)',
                    color: 'white'
                  }}
                >
                  <i className="fas fa-medal fa-3x mb-3 opacity-75"></i>
                  <div className="display-5 fw-bold mb-2">
                    Top {100 - performanceDistribution.predictedPercentile}%
                  </div>
                  <small className="opacity-75">Predicted Percentile</small>
                  <hr className="my-3 opacity-25" />
                  <small className="d-block">
                    You're expected to perform better than {performanceDistribution.predictedPercentile}% of students
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Action Items */}
          <div 
            className="p-4 rounded-4"
            style={{
              background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
              border: '2px solid #667eea30'
            }}
          >
            <h6 className="fw-bold mb-3">
              <i className="fas fa-tasks me-2 text-primary"></i>
              Recommended Actions to Achieve Predicted Score
            </h6>
            <div className="row g-2">
              <div className="col-md-6">
                <div className="d-flex align-items-center p-2 rounded" style={{ background: 'white' }}>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <small>Maintain current study streak (7 days)</small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center p-2 rounded" style={{ background: 'white' }}>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <small>Focus 30% more time on Organic Chemistry</small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center p-2 rounded" style={{ background: 'white' }}>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <small>Complete 3 full-length mock tests per week</small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center p-2 rounded" style={{ background: 'white' }}>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <small>Revise weak chapters every 3 days</small>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="alert alert-info mt-4 mb-0" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <small>
              <strong>Note:</strong> These predictions are based on AI analysis of your recent performance, 
              study patterns, and historical data. Actual results may vary based on exam difficulty and preparation consistency.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveScoreAnalysis;
